import { useCallback, useEffect, useMemo, useState } from "react";
import type { CommentThreadResponse, ContentType } from "../../api/types";
import { getComments } from "../../api/content";
import { buildCommentTree, countNodes, type CommentSort } from "../../lib/comments";
import { CommentNode } from "./CommentNode";
import { CommentForm } from "./CommentForm";
import { Loading, ErrorState } from "../Spinner";

/** How many top-level comments to show before the "show more" fold. */
const ROOTS_PER_PAGE = 20;
/** Comments authored by the site owner are badged "OP". */
const OP_NAME = "csmckay";

interface CommentsProps {
  contentType: ContentType;
  sourceId: number;
}

const SORTS: { value: CommentSort; label: string }[] = [
  { value: "best", label: "Best" },
  { value: "top", label: "Top" },
  { value: "new", label: "Newest" },
  { value: "old", label: "Oldest" },
];

/**
 * The comment thread for one document. Fetches the flat list, assembles the
 * reply tree on the client, and offers sorting, per-thread folding (in
 * CommentNode), and a "show more" fold once there are many top-level comments.
 */
export function Comments({ contentType, sourceId }: CommentsProps) {
  const [thread, setThread] = useState<CommentThreadResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<CommentSort>("best");
  const [visibleRoots, setVisibleRoots] = useState(ROOTS_PER_PAGE);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getComments(contentType, sourceId);
      setThread(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load comments.");
    } finally {
      setLoading(false);
    }
  }, [contentType, sourceId]);

  useEffect(() => {
    load();
  }, [load]);

  const roots = useMemo(
    () => (thread ? buildCommentTree(thread.items, sort) : []),
    [thread, sort],
  );

  // Prefer the live tree count; fall back to the server total before load.
  const total = thread ? countNodes(roots) : 0;
  const shown = roots.slice(0, visibleRoots);
  const remaining = roots.length - shown.length;

  return (
    <section className="comments" aria-label="Comments">
      <div className="comments__head">
        <h2>
          Comments{thread ? ` (${total})` : ""}
        </h2>
        <div className="sort">
          <label htmlFor="comment-sort">Sort:</label>
          <select
            id="comment-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value as CommentSort)}
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <CommentForm
        contentType={contentType}
        sourceId={sourceId}
        onPosted={load}
      />

      {error ? (
        <ErrorState label={error} />
      ) : loading && !thread ? (
        <Loading label="Loading comments…" />
      ) : roots.length === 0 ? (
        <p className="muted">No comments yet. Be the first to say something.</p>
      ) : (
        <>
          <div className="comment-list">
            {shown.map((node) => (
              <CommentNode
                key={node.comment.id}
                node={node}
                contentType={contentType}
                sourceId={sourceId}
                depth={0}
                opName={OP_NAME}
                onReplyPosted={load}
              />
            ))}
          </div>

          {remaining > 0 && (
            <div className="comments__more">
              <button
                className="btn btn--ghost"
                onClick={() => setVisibleRoots((v) => v + ROOTS_PER_PAGE)}
              >
                Show more comments ({remaining} hidden)
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
